/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable max-len */
/** @jsx jsx */
import React, {
  useMemo, useState, useCallback,
} from 'react';
import { css, jsx } from '@emotion/core';
import { Column } from 'react-table';
import { Link } from 'react-router-dom';
import PrimaryButton from 'components/atoms/Button';
import PageTitle from 'components/atoms/PageTitle';
import DataTable from 'components/organismos/DataTable/DataTable';
import Icon from 'components/atoms/Icon';
import { iconColor } from 'components/styles';
import AttendAllowanceDomain from 'domain/master/labor/attendAllowance';
import ConfirmModal from 'components/organismos/ConfirmModal';
import { deleteAllowance } from 'api/attendAllowance';
import useToastNotification from 'hooks/useToastNotification';
import { useSelector } from 'react-redux';
import { Store } from 'modules/store';
import useAttendAllowanceList from './hooks';

/**
 * 手当マスタ一覧（C3_RESDUC-1877）
 * 手本: components/organismos/Executive/ExecutiveIndexPage
 * ※ CSV インポート・ダウンロードは付けない（実装方針で対象外）
 */
const AttendAllowanceIndexLayoutPage: React.FC = () => {
  const { allowanceList, setAllowanceList } = useAttendAllowanceList();
  const [valueAllowanceId, setValue] = useState('');
  const [isHover, setHover] = useState('');
  const [isHover2, setHover2] = useState('');
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { successNotification, errorNotification } = useToastNotification();

  const roleScreen = useSelector((state: Store) => state.auth.roleScreen);

  const closeConfirmModal = useCallback(() => {
    setConfirmModalOpen(false);
  }, []);

  const openConfirmModal = useCallback((item: AttendAllowanceDomain) => {
    setValue(item.allowanceId);
    setConfirmModalOpen(true);
  }, []);

  const deleteAllowanceById = useCallback(() => {
    if (allowanceList) {
      deleteAllowance(valueAllowanceId).then(() => {
        setAllowanceList(allowanceList.filter((allowance) => allowance.allowanceId !== valueAllowanceId));
        setConfirmModalOpen(false);
        successNotification('削除しました。');
      }).catch((error) => {
        setConfirmModalOpen(false);
        if (error.response && error.response.data && error.response.data.errors) {
          let msgError = '';
          error.response.data.errors.map((item: { defaultMessage: string; }) => {
            msgError += `${item.defaultMessage} </br>`;
            return msgError;
          });
          errorNotification(msgError);
        } else {
          errorNotification('サーバー側でエラーが発生しました。');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueAllowanceId, allowanceList]);

  const columns: Array<Column<AttendAllowanceDomain>> = useMemo(() => [
    {
      Header: '手当コード',
      accessor: 'allowanceCode',
      sortType: 'basic',
    },
    {
      Header: '手当名',
      accessor: 'allowanceName',
    },
    {
      Header: '表示メッセージ',
      accessor: 'dispMessage',
    },
    {
      Header: '単位',
      accessor: 'dispUnit',
    },
    {
      Header: () => null,
      accessor: 'action',
      id: 'actionButton',
      disableSortBy: true,
      Cell: (cell: { row: { isExpanded: boolean, original: AttendAllowanceDomain },
        data: Array<AttendAllowanceDomain> }) => (
          <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
            <Link to={`/masterAttendAllowance/edit/${cell.row.original.allowanceId}`}>
              <span
                style={{ marginLeft: '20px' }}
                onMouseOver={() => { setHover(cell.row.original.allowanceId); }}
                onMouseOut={() => { setHover(''); }}
              >
                <Icon type="edit" color={(isHover && isHover === cell.row.original.allowanceId) ? iconColor.grayhover : iconColor.gray} />
              </span>
            </Link>
            {
              roleScreen && roleScreen.editable === 1 && (
                <span
                  style={{ marginLeft: '20px', marginRight: '10px' }}
                  role="presentation"
                  onMouseOver={() => { setHover2(cell.row.original.allowanceId); }}
                  onFocus={() => {}}
                  onMouseOut={() => { setHover2(''); }}
                  onClick={() => { openConfirmModal(cell.row.original); }}
                  onBlur={() => {}}
                >
                  <Icon type="delete" color={(isHover2 && isHover2 === cell.row.original.allowanceId) ? iconColor.grayhover : iconColor.gray} />
                </span>
              )
            }
          </div>
      ),
    },
  ], [isHover, isHover2, openConfirmModal, roleScreen]);

  const sortBy = React.useMemo(
    () => [
      {
        id: 'allowanceCode',
        desc: false,
      },
    ],
    [],
  );

  return (
    <React.Fragment>
      <div>
        <PageTitle
          title=""
          rightContents={
            roleScreen && roleScreen.editable === 1 && (
              <Link to="/masterAttendAllowance/add">
                <PrimaryButton
                  ghost={false}
                  text="新規手当マスタ作成"
                />
              </Link>
            )
          }
        />

        <div
          css={css`
            table th:nth-child(1) {
              width: 100px;
            }
            table th:nth-child(2) {
              width: 200px;
            }
            table th:nth-child(3) {
              width: 300px;
            }
            table th:nth-child(4) {
              width: 80px;
            }
            table th:nth-child(5) {
              width: 80px;
            }
          `}
        >
          <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', margin: '15px' }}>
            <DataTable
              columns={columns}
              data={allowanceList}
              isGlobalFilter={true}
              sortBy={sortBy}
            />
          </div>
        </div>

        {/* confirm modal */}
        <ConfirmModal
          open={confirmModalOpen}
          closeHandler={closeConfirmModal}
          title="確認メッセージ"
          content="削除します。よろしいですか？"
          submitText="削除"
          onSubmit={deleteAllowanceById}
          items={[]}
        />
      </div>
    </React.Fragment>
  );
};

export default AttendAllowanceIndexLayoutPage;
